import { createQueryClient } from '@cobliteam/api/queryClient';
import { ldInitialContext } from '@cobliteam/hooks/src/useFeatureFlags/ldBootContext';
import { I18nextProvider, i18nConfig } from '@cobliteam/i18n';
import { PermissionsProvider } from '@cobliteam/permissions';
import { Environment } from '@cobliteam/react-environments';
import { TooltipProvider as LegacyTooltipProvider } from '@cobliteam/ui/components/Tooltip';
import { basicLogger } from '@launchdarkly/js-client-sdk';
import { createLDReactProvider, useLDClient } from '@launchdarkly/react-sdk';
import { QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { Suspense, useEffect } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { Router as RouterProvider } from 'react-router-dom';
import { toast } from 'sonner';
import AppToaster from './components/AppToaster/AppToaster';
import DemoBanner from './components/DemoBanner/DemoBanner';
import InitialLoadingPage from './pages/LoadingPage/InitialLoadingPage';
import Router from './Router';
import { history, store } from './store';

const env = Environment.getInstance().getEnvironment();

const IS_DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

// O namespace da chave pode não ter sido carregado por nenhuma tela ainda; sem ele, o i18next devolve a chave.
const translateLoadingNamespace = async (key: string) => {
  const [namespace] = key.split(':');
  if (key.includes(':')) await i18nConfig.loadNamespaces(namespace);
  return i18nConfig.t(key);
};

export const queryClient = createQueryClient({
  notify: { error: (message) => toast.error(message), success: (message) => toast.success(message) },
  translate: translateLoadingNamespace,
});

const Dashboard = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ReduxProvider store={store}>
        <I18nextProvider i18n={i18nConfig}>
          {/* O legado fica só para os tooltips Radix internos do @cobliteam/ui (CopyButton, VideoPlayer, Slider, FileUpload). */}
          <LegacyTooltipProvider delayDuration={300}>
            <PermissionsProvider>
              <RouterProvider history={history}>
                <NuqsAdapter>
                  {/*
                   * Boundary raiz. O único Suspense do conteúdo vive dentro do `AppLayout`
                   * (`AppContentLoadingBoundary`), então nada segurava uma suspensão acima dele:
                   * nem os providers de rota privada, nem o corpo do próprio `AppLayout`
                   * (header/sidebar chamam `useTranslation`, e o i18next roda com `useSuspense`
                   * default). Sem boundary acima, suspender num sync lane — update de store
                   * externo durante o commit — faz o React lançar o erro #426 ("A component
                   * suspended while responding to synchronous input"), que sobe até a raiz e
                   * desmonta o app inteiro: tela presa no loader do index.html. Com a boundary
                   * o React mostra o fallback em vez de lançar. `InitialLoadingPage` renderiza
                   * null enquanto o loader estático ainda está na tela, então não duplica loader.
                   */}
                  <Suspense fallback={<InitialLoadingPage />}>
                    <Router />
                  </Suspense>
                  <AppToaster />
                  <DemoBanner />
                </NuqsAdapter>
              </RouterProvider>
            </PermissionsProvider>
          </LegacyTooltipProvider>
        </I18nextProvider>
      </ReduxProvider>
    </QueryClientProvider>
  );
};

// v4 substitui o HOC `withLDProvider` por um Provider retornado por `createLDReactProvider`.
// O client nasce com o último contexto identificado neste navegador (ou anônimo no primeiro
// acesso/login): a avaliação de flags corre em paralelo com session/getUserData, e o cache local
// do SDK guarda as flags desse contexto. O `FeatureFlagsProvider` só chama `identify()` quando o
// contexto real difere dele (primeiro acesso, troca de usuário ou de frota).
const LDProvider = createLDReactProvider(env.launchDarklyId, ldInitialContext, {
  ldOptions: {
    logger: basicLogger({ level: 'none' }),
    sendEvents: !IS_DEMO_MODE,
    // Demo: LaunchDarkly is fully mocked by MSW (libs/msw-demo). Force polling
    // (`streaming: false`) so the SDK only issues eval GETs — MSW can't
    // intercept EventSource streaming, which would hit real LD and
    // reconnect-loop. Analytics/diagnostics are silenced so nothing escapes to
    // `*.launchdarkly.com`.
    // `fetchGoals` (experimentation por clique/pageview) é disparado na CRIAÇÃO do client,
    // não no `start()`, então acontece no module-eval — antes do MSW existir — e o GET de
    // `/sdk/goals/demo-dummy` escapa para o LD real e toma 404. Demo não usa experimentation.
    ...(IS_DEMO_MODE && { streaming: false, diagnosticOptOut: true, fetchGoals: false }),
  },
  // Demo: este módulo é avaliado pelo import estático em index.tsx, ou seja ANTES de
  // `startDemoWorker()`. Com o auto-start default o client dispara o primeiro GET de
  // evalx sem o service worker do MSW ativo, escapa para o LD real e toma 401
  // (`demo-dummy` não é client ID válido). Adiando o start para a montagem do provider
  // — já com o worker ativo — o handler de libs/msw-demo responde.
  ...(IS_DEMO_MODE && { deferInitialization: true }),
});

/**
 * Dá o `start()` que o `deferInitialization` adiou. Só monta em demo.
 *
 * Effect (e não chamada em render) porque `start()` é side effect imperativo num sistema
 * externo; é idempotente no SDK (`start()` devolve a `startPromise` existente), então o
 * double-invoke do StrictMode não abre dois clients.
 */
const DemoLDStarter = () => {
  const ldClient = useLDClient();

  useEffect(() => {
    ldClient.start();
  }, [ldClient]);

  return null;
};

// O provider é montado em TODOS os modos, inclusive demo. Em demo o `.env.demo` passa
// `REACT_APP_LAUNCH_DARKLY_ID=demo-dummy`, o provider roda em polling e o MSW responde o
// endpoint de eval (ver libs/msw-demo/src/handlers/launchDarkly.ts); o `FeatureFlagsProvider`
// curto-circuita o `identify()`. Montar sempre é obrigatório porque `useLDClient()`/`useFlags()`
// (LD React SDK v4) fazem `useContext` + destructuring do contexto; sem provider o contexto é
// `null` e o app quebra na primeira leitura de flag.
const DashboardWithLaunchDarkly = () => (
  <LDProvider>
    {IS_DEMO_MODE && <DemoLDStarter />}
    <Dashboard />
  </LDProvider>
);

export default DashboardWithLaunchDarkly;
